import * as Types from '../types';
import { Environment } from '../../../config';
import request from '../../../utils/request';

/** fetchNotificationList  */
interface IDataItems {
  notification_id: number;
  applicant_id: number;
  notification_type: string;
  notification_title: string;
  notification_message: string;
  is_read: number;
  created_by: string;
  created_at: string;
  modified_by: string;
  modified_at: string;
}

interface ISorting {
  sortField: string;
  order: 'asc' | 'desc';
}

interface IPaging {
  page: string;
  pageSize: string;
  total: number;
}

interface INotificationListOutput {
  items: IDataItems[];
  paging: IPaging;
  sorting: ISorting;
}

interface INotificationListInput {
  sortField: string;
  page: number;
  pageSize: number;
  order: 'asc' | 'desc';
  vatNumber?: string | null;
  notificationType?: string | null;
  notificationDateFrom?: string | null;
  notificationDateTo?: string | null;
}

export const fetchNotificationList = async (
  payload: INotificationListInput
): Promise<Types.ResponseOutput> => {
  let query = `page=${payload.page}&pageSize=${payload.pageSize}&sortField=${payload.sortField}&order=${payload.order}`;
  if (payload.vatNumber)
    query += `&vatNumber=${encodeURIComponent(payload.vatNumber)}`;
  if (payload.notificationType)
    query += `&notificationType=${encodeURIComponent(
      payload.notificationType
    )}`;
  if (payload.notificationDateFrom)
    query += `&notificationDateFrom=${encodeURIComponent(
      payload.notificationDateFrom
    )}`;
  if (payload.notificationDateTo)
    query += `&notificationDateTo=${encodeURIComponent(
      payload.notificationDateTo
    )}`;
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/notification/list?${query}`
  );

  let _data: INotificationListOutput = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

/** Notification Detail */
interface INotificationDetailInput {
  notification_id: number;
}

interface INotificationDetailOutput extends Types.ResponseOutput {
  data: {
    notification_id: number | null;
    applicant_id: number | null;
    notification_type: string;
    notification_title: string;
    notification_message: string;
    is_read: number | null;
    created_by: string;
    created_at: string;
    modified_by: string;
    modified_at: string;
  };
}

export const fetchNotificationDetail = async (
  payload: INotificationDetailInput
): Promise<INotificationDetailOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/notification/${payload.notification_id}`
  );

  let output = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

/** Add Notification */
interface INotificationAddInput {
  applicant_id: number | null;
  notification_type: string;
  notification_title: string;
  notification_message: string;
  is_read: number | null;
  created_by: string;
  created_at: string;
  modified_by: string;
  modified_at: string;
}

interface INotificationAddOutput extends Types.ResponseOutput {
  data: {};
}

export const addNotification = async (
  payload: INotificationAddInput
): Promise<INotificationAddOutput> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/notification `,
    payload
  );

  let output = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

/** Edit Notification */
interface INotificationEditInput {
  applicant_id: number | null;
  notification_type: string;
  notification_title: string;
  notification_message: string;
  is_read: number | null;
  created_by: string;
  created_at: string;
  modified_by: string;
  modified_at: string;
}

interface INotificationEditOutput extends Types.ResponseOutput {
  data: {};
}

export const editNotification = async (
  payload: INotificationEditInput
): Promise<INotificationEditOutput> => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/notification `,
    payload
  );

  let output = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export interface FetchReminderListInput {
  sortField: string;
  page: number;
  pageSize: number;
  order: 'asc' | 'desc';
  vatNumber?: string | null;
  period?: string | null;
  remindedDateFrom?: string | null;
  remindedDateTo?: string | null;
}

export interface ReminderListElement {
  applicant_id: number;
  vat_number: string;
  is_sent: boolean;
  type: string;
  reminded_date: Date;
  reminded_sernder: string;
  period: string;
}

export interface ReminderListPaging {
  page: number;
  pageSize: number;
  total: number;
}

export interface ReminderListData {
  paging: ReminderListPaging;
  items: ReminderListElement[];
}

export const fetchReminderList = async (
  payload: FetchReminderListInput
): Promise<Types.ResponseOutput> => {
  let query = `page=${payload.page}&pageSize=${payload.pageSize}&sortField=${payload.sortField}&order=${payload.order}`;
  if (payload.vatNumber)
    query += `&vatNumber=${encodeURIComponent(payload.vatNumber)}`;
  if (payload.period) query += `&period=${encodeURIComponent(payload.period)}`;
  if (payload.remindedDateFrom)
    query += `&remindedDateFrom=${encodeURIComponent(
      payload.remindedDateFrom
    )}`;
  if (payload.remindedDateTo)
    query += `&remindedDateTo=${encodeURIComponent(payload.remindedDateTo)}`;
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/reminder/list?${query}`
  );

  let _data: ReminderListData = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const setNotificationView = async (
  notificationId: number
): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/notification/view/${notificationId}`
  );
  let output: Types.ResponseOutput = {
    message: data.message,
    data,
    status: data.status,
  };
  return output;
};

interface IUnreadNotificationCountOutput {
  total: number;
}

export const fetchUnreadNotificationCount =
  async (): Promise<Types.ResponseOutput> => {
    const { data }: any = await request(
      'get',
      `${Environment.API_BASE_URL}/api/notification/unread`
    );

    let _data: IUnreadNotificationCountOutput = data.data;
    let output: Types.ResponseOutput = {
      message: data.message,
      data: _data,
      status: data.status,
    };
    return output;
  };
